import { motion } from "framer-motion";
import Chapter, { FadeIn } from "./Chapter";

const EASE = [0.16, 1, 0.3, 1];

const TOP_SALSA = [
  { name: "Taqueria De Diez", score: 10 },
  { name: "Feliz Mango Taqueria", score: 9.7 },
  { name: "Burrito Factory", score: 9.2 },
  { name: "Tacos Las Amazonas #1", score: 9.1 },
  { name: "Oye Taquito 956", score: 9.0 },
];

const GapBar = ({ label, value, max, accent }) => (
  <div>
    <div className="flex justify-between font-mono text-[10px] tracking-[0.14em] uppercase mb-1">
      <span className="text-ink2">{label}</span>
      <span className={`font-bold ${accent ? "text-salsa" : ""}`}>{value}</span>
    </div>
    <div className="score-track">
      <motion.div
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: value / max }}
        viewport={{ once: true }}
        transition={{ duration: 1, ease: EASE }}
        className={`score-fill ${accent ? "accent" : ""}`}
      />
    </div>
  </div>
);

export const Findings = () => (
  <div id="the-findings" className="scroll-mt-14">
    <Chapter no="05" kicker="Finding One" title="The Salsa & Tortilla Multiplier" />
    <section className="px-4 sm:px-8 pb-24">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-8">
          <FadeIn className="font-body text-base md:text-lg leading-relaxed text-ink2 space-y-5 max-w-2xl">
            <p>
              The data supports the hypothesis. Every spot scoring 9.0 or above has at least one of:
              housemade salsa scoring 9.0+, a handmade tortilla at 8.5+, or a specialty slow-cooked
              protein. Every spot scoring below 8.0 lacks <em>both</em> housemade salsa and handmade
              tortillas.
            </p>
            <p>
              Housemade salsa requires daily preparation and fresh ingredients — it reflects the cook's
              palate and cannot be bought in bulk. Handmade tortillas are pressed fresh daily and hold
              fillings differently, absorb salsa differently. They change the entire taco. The
              multiplier effect is real, and the data proves it.
            </p>
          </FadeIn>
          <FadeIn className="grid sm:grid-cols-2 border-2 border-ink">
            <div className="p-6 border-b-2 sm:border-b-0 sm:border-r-2 border-ink">
              <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-salsa mb-3">Every spot ≥ 9.0 has</p>
              <ul className="font-mono text-xs space-y-2">
                <li>+ Housemade salsa (9.0+)</li>
                <li>+ Handmade tortilla (8.5+)</li>
                <li>+ Slow-cooked specialty protein</li>
              </ul>
            </div>
            <div className="p-6">
              <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-muted2 mb-3">Every spot &lt; 8.0 lacks</p>
              <ul className="font-mono text-xs space-y-2 text-ink2">
                <li>× Housemade salsa</li>
                <li>× Handmade tortillas</li>
                <li>× Both. Without exception.</li>
              </ul>
            </div>
          </FadeIn>
        </div>
        <FadeIn delay={0.1} className="lg:col-span-5">
          <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-muted2 mb-4">
            Top 5 salsa scores — all five spots rank in the top 10
          </p>
          <div className="space-y-5" data-testid="salsa-leaderboard">
            {TOP_SALSA.map((s, i) => (
              <div key={s.name}>
                <div className="flex justify-between font-mono text-[10px] tracking-[0.12em] uppercase mb-1">
                  <span>{String(i + 1).padStart(2, "0")} — {s.name}</span>
                  <span className="font-bold text-salsa">{s.score.toFixed(1)}</span>
                </div>
                <div className="score-track">
                  <motion.div
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: s.score / 10 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, ease: EASE, delay: i * 0.08 }}
                    className="score-fill accent"
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 border-2 border-ink p-6 bg-paper2" data-testid="multiplier-note">
            <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-salsa mb-2">Note</p>
            <p className="font-serif italic text-xl md:text-2xl leading-snug">
              "Housemade salsa cannot be bought in bulk or standardized. It is the clearest signal of a
              kitchen that still cooks."
            </p>
          </div>
        </FadeIn>
      </div>
    </section>

    <Chapter no="06" kicker="Finding Two" title="The Authenticity Gap" />
    <section className="px-4 sm:px-8 pb-24">
      <FadeIn className="max-w-2xl font-body text-base md:text-lg leading-relaxed text-ink2 mb-10">
        <p>
          Paid promotion doesn't create fake reviews — it creates a cycle: visibility creates volume,
          volume creates reviews, reviews create more visibility. A feedback loop disconnected from
          food quality, systematically disadvantaging the family truck that can't afford to play.
        </p>
      </FadeIn>
      <div className="grid md:grid-cols-3 gap-6" data-testid="gap-exhibits">
        <FadeIn className="border-2 border-ink p-6 flex flex-col" >
          <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-muted2 mb-4">Exhibit A — The Hyped</p>
          <p className="font-serif font-semibold text-3xl">Veracruz All Natural</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink2 mt-1 mb-6">
            Rachael Ray endorsed · national press · multiple locations
          </p>
          <div className="space-y-4 mt-auto">
            <GapBar label="Online perception" value={9.0} max={10} />
            <GapBar label="In-person composite" value={7.8} max={10} accent />
          </div>
          <p className="mt-4 font-mono text-[10px] text-salsa uppercase tracking-[0.12em]">
            Largest gap in the study
          </p>
        </FadeIn>
        <FadeIn delay={0.08} className="border-2 border-ink p-6 flex flex-col">
          <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-muted2 mb-4">Exhibit B — The Trap</p>
          <p className="font-serif font-semibold text-3xl">Taco Haus</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink2 mt-1 mb-6">
            4.7 Google rating · Westin food court foot traffic
          </p>
          <div className="space-y-4 mt-auto">
            <GapBar label="Online rating (×2)" value={9.4} max={10} />
            <GapBar label="In-person composite" value={6.47} max={10} accent />
          </div>
          <p className="mt-4 font-mono text-[10px] text-salsa uppercase tracking-[0.12em]">
            Nearly a 3-point gap. Algorithmic mediocrity.
          </p>
        </FadeIn>
        <FadeIn delay={0.16} className="border-2 border-salsa p-6 flex flex-col bg-paper2">
          <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-salsa mb-4">Exhibit C — The Real No. 1</p>
          <p className="font-serif font-semibold text-3xl">Feliz Mango Taqueria</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink2 mt-1 mb-6">
            29 Yelp reviews · an obscure truck · zero marketing
          </p>
          <div className="space-y-4 mt-auto">
            <GapBar label="Online footprint" value={2.9} max={10} />
            <GapBar label="In-person composite" value={9.6} max={10} accent />
          </div>
          <p className="mt-4 font-mono text-[10px] text-salsa uppercase tracking-[0.12em]">
            The best taco in Austin is the hardest to find online.
          </p>
        </FadeIn>
      </div>
    </section>

    <Chapter no="07" kicker="Finding Three" title="The Real Value Equation" />
    <section className="px-4 sm:px-8 pb-28">
      <div className="grid lg:grid-cols-12 gap-10 items-end">
        <FadeIn className="lg:col-span-5">
          <p className="font-serif font-semibold tracking-tight leading-[0.9] text-7xl sm:text-8xl" data-testid="value-figure">
            $3.50<span className="text-salsa">–</span>$7
          </p>
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted2 mt-3">
            Per taco, everywhere on the approved list
          </p>
        </FadeIn>
        <FadeIn delay={0.1} className="lg:col-span-7 font-body text-base md:text-lg leading-relaxed text-ink2 space-y-5">
          <p>
            La Casita del Pastor sells three tacos for $10. Oye Taquito 956 sells six mini tacos as a
            combo. Taquito Aviles serves the city's best menudo at an accessible price. Meanwhile the
            trendier spots charge $8 and up per taco — a full meal for two approaching $50.
          </p>
          <p className="font-serif italic text-2xl md:text-3xl text-ink border-l-2 border-salsa pl-6">
            The most authentic spots are almost always the most affordable. No publicists, no Instagram
            managers, no expensive dining rooms — every dollar goes into the food.
          </p>
        </FadeIn>
      </div>
    </section>
  </div>
);

export default Findings;
